import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

export type MoveAssetInput = {
  id: string;
  parentAssetId: string | null;
};

// PATCH /api/admin/content/assets/:id/parent
//
// Replaces revalidatePath("/admin/content") from
// /app/admin/content/_actions.ts::moveContentAsset. Non-optimistic — drag-
// and-drop UIs can layer their own optimistic state on top via setQueryData
// before calling mutate, but the hook stays simple.
export function useMoveAsset() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, MoveAssetInput>({
    mutationFn: ({ id, parentAssetId }) =>
      apiFetch<{ ok: true }>(`/admin/content/assets/${id}/parent`, {
        method: "PATCH",
        body: JSON.stringify({ parent_asset_id: parentAssetId }),
      }),
    onSuccess: (_data, { id }) => {
      // Move sweeps two folders (source + destination). The parameterised
      // list keys all start with `["content", "list"]` so a single bare
      // invalidate covers every folder-scoped cache; the detail key for
      // the moved asset also needs to drop its stale `parent_asset_id`.
      queryClient.invalidateQueries({ queryKey: queryKeys.content.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.content.detail(id) });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to move asset");
    },
  });
}
